<template>
  <el-select
    :value="value"
    :placeholder="placeholder"
    :style="selectStyle"
    :popper-class="popperClass"
    :class="selectClass"
    filterable
    remote
    clearable
    :remote-method="handleSearch"
    :loading="selectLoading"
    @input="handleInput"
    @change="handleChange"
    @clear="handleClear"
    @visible-change="handleVisibleChange"
  >
    <el-option
      v-for="student in options"
      :key="student.id"
      :label="formatStudentOptionLabel(student)"
      :value="student.id"
    >
      <span class="student-option-text" :title="formatStudentOptionLabel(student)">
        {{ formatStudentOptionLabel(student) }}
      </span>
    </el-option>
    <div class="student-select-pager" @mousedown.prevent @click.stop>
      <el-button
        size="mini"
        :disabled="page <= 1 || loading"
        @click.stop="changePage(page - 1)"
      >上一页</el-button>
      <span class="student-select-pager__text">
        <span v-if="loading">查询中...</span>
        <span v-else>第 {{ page }} / {{ totalPages }} 页（共{{ total }}条）</span>
      </span>
      <el-button
        size="mini"
        :disabled="page >= totalPages || loading"
        @click.stop="changePage(page + 1)"
      >下一页</el-button>
    </div>
  </el-select>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'UtwlStudentSelect',
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    placeholder: {
      type: String,
      default: '请输入学生姓名搜索'
    },
    width: {
      type: [String, Number],
      default: 200
    },
    pageSize: {
      type: Number,
      default: 20
    },
    popperClass: {
      type: String,
      default: 'student-select-popper'
    },
    errorMessage: {
      type: String,
      default: '加载学生失败'
    },
    searchParam: {
      type: String,
      default: 'studentName'
    },
    selectClass: {
      type: [String, Object, Array],
      default: ''
    },
    apiPath: {
      type: String,
      default: '/student/location-options'
    }
  },
  data() {
    return {
      options: [],
      loading: false,
      keyword: '',
      page: 1,
      total: 0,
      loadRequestSeq: 0
    };
  },
  computed: {
    selectStyle() {
      const width = typeof this.width === 'number' ? `${this.width}px` : this.width;
      return width ? { width } : null;
    },
    totalPages() {
      const total = Number(this.total || 0);
      const size = Number(this.pageSize || 20);
      return Math.max(1, Math.ceil(total / size));
    },
    selectLoading() {
      return this.loading && (!Array.isArray(this.options) || this.options.length === 0);
    }
  },
  methods: {
    handleInput(value) {
      this.$emit('input', value);
    },
    handleChange(value) {
      const selectedStudent = this.findStudentById(value);
      this.$emit('change', value, selectedStudent);
      this.$emit('selected-student', selectedStudent);
    },
    findStudentById(value) {
      if (!value) return null;
      return (this.options || []).find(item => String(item.id) === String(value)) || null;
    },
    formatStudentOptionLabel(student) {
      if (!student) return '';
      const name = student.studentName != null ? String(student.studentName).trim() : '';
      const cityName = student.cityName != null ? String(student.cityName).trim() : '';
      if (!cityName) return name;
      if (!name) return `（${cityName}）`;
      return `${name}（${cityName}）`;
    },
    normalizeStudent(item) {
      return {
        id: item.id,
        studentName: item.studentName || '',
        provinceCode: item.provinceCode || '',
        cityCodeRegion: item.cityCodeRegion || '',
        districtCode: item.districtCode || '',
        provinceName: item.provinceName || '',
        cityName: item.cityName || '',
        districtName: item.districtName || '',
        addressDetail: item.addressDetail || ''
      };
    },
    async loadOptions(page = 1, keyword = this.keyword) {
      const requestSeq = this.loadRequestSeq + 1;
      this.loadRequestSeq = requestSeq;
      this.loading = true;
      try {
        const nextPage = Math.max(1, Number(page || 1));
        const nextKeyword = String(keyword || '').trim();
        const keywordPayload = nextKeyword ? { [this.searchParam || 'studentName']: nextKeyword } : {};
        const res = await api.post(this.apiPath || '/student/location-options', {
          page: nextPage,
          pageSize: this.pageSize,
          ...keywordPayload
        });
        if (requestSeq !== this.loadRequestSeq) return;
        if (res.data.success) {
          const data = res.data.data || {};
          this.page = Number(data.page || nextPage) || nextPage;
          this.total = Number(data.total || 0) || 0;
          this.keyword = nextKeyword;
          const rows = Array.isArray(data.list) ? data.list : [];
          this.options = rows.map(this.normalizeStudent);
        }
      } catch (e) {
        if (requestSeq === this.loadRequestSeq) {
          console.error(this.errorMessage, e);
        }
      } finally {
        if (requestSeq === this.loadRequestSeq) {
          this.loading = false;
        }
      }
    },
    handleSearch(query) {
      this.keyword = String(query || '').trim();
      this.loadOptions(1, this.keyword);
    },
    handleClear() {
      this.keyword = '';
      this.loadOptions(1, '');
    },
    changePage(page) {
      const nextPage = Math.max(1, Number(page || 1));
      if (nextPage === this.page) return;
      this.loadOptions(nextPage, this.keyword);
    },
    handleVisibleChange(visible) {
      if (!visible) return;
      if (!Array.isArray(this.options) || this.options.length === 0) {
        this.loadOptions(1, this.keyword);
      }
    }
  }
};
</script>

<style>
.student-select-popper {
  width: 350px !important;
  max-width: 350px !important;
}

.student-select-popper .el-select-dropdown__list {
  max-width: 100%;
}

.student-select-popper .el-select-dropdown__wrap {
  margin-bottom: 0 !important;
  overflow-x: hidden;
}

.student-select-popper .el-scrollbar__bar.is-horizontal {
  display: none;
}

.student-select-popper .el-select-dropdown__item {
  display: flex;
  align-items: center;
  max-width: 100%;
}

.student-select-popper .student-option-text {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-select-popper .student-select-pager {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}

.student-select-popper .student-select-pager__text {
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
}
</style>
